<template>
  <section>
    <!-- Hero -->
    <base-page-heading :title="$t('admin.users.title')">
      <template #extra>
        <b-breadcrumb class="breadcrumb-alt">
          <b-breadcrumb-item>
            <router-link to="/">{{ $t("layout.breadcrumb.home") }}</router-link>
          </b-breadcrumb-item>
          <b-breadcrumb-item>
            {{ $t("layout.breadcrumb.admin") }}
          </b-breadcrumb-item>
          <b-breadcrumb-item active
            >{{ $t("admin.users.title") }}
          </b-breadcrumb-item>
        </b-breadcrumb>
      </template>
    </base-page-heading>
    <!-- END Hero -->

    <!-- Delete Block Modal -->
    <b-modal
      id="modal-block-delete"
      body-class="p-0"
      hide-footer
      hide-header
      v-if="dropUser"
    >
      <div class="block block-themed block-transparent mb-0">
        <div class="block-header bg-primary-dark">
          <h3 class="block-title">
            {{ $t("admin.users.modal.delete.title") }}
          </h3>
          <div class="block-options">
            <button
              type="button"
              class="btn-block-option"
              @click="$bvModal.hide('modal-block-delete')"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>
        <div class="block-content font-size-sm">
          <p>
            {{
              $t("admin.users.modal.delete.msg", {
                user: dropUser.username
              })
            }}
          </p>
        </div>
        <div class="block-content block-content-full text-right border-top">
          <b-button
            variant="light"
            size="sm"
            @click="$bvModal.hide('modal-block-delete')"
            >{{ $t("admin.users.modal.delete.no") }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="removeUser(dropUser.username)"
          >
            <i class="fa fa-check mr-1"></i>
            {{ $t("admin.users.modal.delete.yes") }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- END Delete Block Modal -->

    <div class="content content-narrow">
      <b-row>
        <b-col>
          <base-block
            :title="$t('admin.users.table.title')"
            header-bg
            content-full
          >
            <b-table-simple
              responsive
              striped
              hover
              borderless
              class="table-vcenter font-size-sm mb-0"
            >
              <b-thead class="text-left">
                <b-tr>
                  <b-th></b-th>
                  <b-th class="font-w700"
                    >{{ $t("admin.users.table.username") }}
                  </b-th>
                  <b-th>{{ $t("admin.users.table.name") }}</b-th>
                  <b-th>{{ $t("admin.users.table.role") }}</b-th>
                  <b-th>{{ $t("admin.users.table.password") }}</b-th>
                  <b-th class="d-sm-table-cell font-w700"
                    >{{ $t("admin.users.table.action") }}
                  </b-th>
                </b-tr>
              </b-thead>
              <b-tfoot>
                <b-tr>
                  <b-td></b-td>
                  <b-td>
                    <b-form-input
                      type="text"
                      v-model="newUser.username"
                      :placeholder="
                        $t('admin.users.table.placeholders.username')
                      "
                      :state="
                        $v.newUser.username.$dirty
                          ? !$v.newUser.username.$error
                          : null
                      "
                      @keyup.enter="addUser"
                    />
                  </b-td>
                  <b-td>
                    <b-form-input
                      type="text"
                      v-model="newUser.name"
                      :placeholder="$t('admin.users.table.placeholders.name')"
                      @keyup.enter="addUser"
                    />
                  </b-td>
                  <b-td>
                    <b-form-select v-model="newUser.role">
                      <option value="admin">admin</option>
                      <option value="manage">manage</option>
                      <option value="view">view</option>
                    </b-form-select>
                  </b-td>
                  <b-td>
                    <b-form-input
                      type="text"
                      v-model="newUser.password"
                      :placeholder="
                        $t('admin.users.table.placeholders.newPassword')
                      "
                      :state="
                        $v.newUser.password.$dirty
                          ? !$v.newUser.password.$error
                          : null
                      "
                      @keyup.enter="addUser"
                    />
                  </b-td>
                  <b-td>
                    <b-button size="sm" variant="alt-primary" @click="addUser">
                      <i class="fa fa-fw fa-plus-square"></i>
                    </b-button>
                  </b-td>
                </b-tr>
              </b-tfoot>
              <b-tbody>
                <b-tr v-for="(user, index) in users" :key="index">
                  <b-td>
                    <b-button
                      v-if="user.username !== 'admin'"
                      size="sm"
                      class="button-margin"
                      variant="alt-danger"
                      v-b-modal.modal-block-delete
                      @click="dropUser = user"
                    >
                      <i class="fa fa-fw fa-times"></i>
                    </b-button>
                  </b-td>
                  <b-td class="text-left">
                    {{ user.username }}
                  </b-td>
                  <b-td class="text-left">
                    <span v-if="editUser !== index">
                      {{ user.name }}
                    </span>
                    <b-form-input
                      v-else
                      type="text"
                      v-model="users[index].name"
                      @keyup.enter="updateUser"
                    />
                  </b-td>
                  <b-td class="text-left">
                    <span v-if="editUser !== index">
                      {{ user.role }}
                    </span>
                    <b-form-select v-else v-model="users[index].role">
                      <option value="admin">admin</option>
                      <option value="manage">manage</option>
                      <option value="view">view</option>
                    </b-form-select>
                  </b-td>
                  <b-td class="text-left">
                    <span v-if="editUserCredentials !== index">
                      *****
                    </span>
                    <template v-else>
                      <b-form-input
                        type="text"
                        v-model="users[index].password"
                        :placeholder="
                          $t('admin.users.table.placeholders.newPassword')
                        "
                        @keyup.enter="updateUserCredentials"
                      />
                      <br />
                      <b-form-input
                        type="text"
                        v-model="users[index].password_re"
                        :placeholder="
                          $t('admin.users.table.placeholders.newPasswordRe')
                        "
                        @keyup.enter="updateUserCredentials"
                      />
                    </template>
                  </b-td>
                  <b-td class="d-sm-table-cell text-nowrap">
                    <template
                      v-if="editUser !== index && editUserCredentials !== index"
                    >
                      <b-button
                        size="sm"
                        variant="alt-primary"
                        @click="editUser = index"
                      >
                        <i class="fa fa-fw fa-pencil-alt"></i>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="alt-primary"
                        class="button-margin"
                        @click="editUserCredentials = index"
                      >
                        <i class="fab fa-fw fa-expeditedssl"></i>
                      </b-button>
                    </template>
                    <template v-else-if="editUser === index">
                      <b-button
                        size="sm"
                        variant="alt-primary"
                        @click="updateUser"
                      >
                        <i class="fa fa-fw fa-save"></i>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="alt-secondary"
                        @click="editUser = null"
                        class="button-margin"
                      >
                        <i class="fa fa-fw fa-window-close"></i>
                      </b-button>
                    </template>
                    <template v-else-if="editUserCredentials === index">
                      <b-button
                        size="sm"
                        variant="alt-primary"
                        @click="updateUserCredentials"
                      >
                        <i class="fa fa-fw fa-save"></i>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="alt-secondary"
                        @click="editUserCredentials = null"
                        class="button-margin"
                      >
                        <i class="fa fa-fw fa-window-close"></i>
                      </b-button>
                    </template>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </base-block>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
// @ is an alias to /src
import Loading from "@/store/loading";
import Notification from "@/assets/js/Notification";
import Auth from "@/assets/js/Auth";
import Users from "@/assets/js/Users";

import { validationMixin } from "vuelidate";
import { required, alphaNum, minLength } from "vuelidate/lib/validators";

export default {
  name: "Users",
  mixins: [validationMixin],
  data() {
    return {
      editUser: null,
      dropUser: null,
      editUserCredentials: null,
      newUser: {
        username: "",
        name: "",
        role: "view",
        password: ""
      },
      users: [],
      Auth: Auth
    };
  },
  validations: {
    newUser: {
      username: {
        required,
        alphaNum,
        minLength: minLength(3)
      },
      password: {
        required,
        minLength: minLength(8)
      }
    }
  },
  beforeMount() {
    if (!Auth.isUserAdmin()) {
      this.$router.push("/");
    }
  },
  mounted() {
    if (Auth.isUserAdmin()) {
      document.title = this.$t("admin.users.title");

      Notification.init(this);

      this.getUserList();
    }
  },
  methods: {
    getUserList: function() {
      Loading.dispatch("increase");
      Users.getList().then(userList => {
        this.users = userList;
        Loading.dispatch("reduce");
      });
    },
    addUser: function() {
      if (Auth.isUserAdmin()) {
        this.$v.newUser.$touch();

        if (this.$v.newUser.username.$anyError) {
          Notification.push(
            "",
            this.$t("admin.users.notifications.missingDataForAddUser"),
            "danger"
          );
        } else {
          Users.addUser(
            this.newUser.username,
            this.newUser.name,
            this.newUser.role,
            this.newUser.password
          ).then(result => {
            if (!result) {
              Notification.push(
                "",
                this.$t("admin.users.notifications.notAdded"),
                "danger"
              );
            } else {
              Notification.push(
                "",
                this.$t("admin.users.notifications.added"),
                "success"
              );

              this.newUser.username = "";
              this.newUser.name = "";
              this.newUser.role = "view";
              this.newUser.password = "";

              this.getUserList();
            }
            Loading.dispatch("reduce");
          });
        }
      }
    },
    removeUser: function(username) {
      if (Auth.isUserAdmin()) {
        this.$bvModal.hide("modal-block-delete");
        Loading.dispatch("increase");
        Users.removeUser(username).then(result => {
          if (!result) {
            Notification.push(
              "",
              this.$t("admin.users.notifications.notDeleted"),
              "danger"
            );
          } else {
            Notification.push(
              "",
              this.$t("admin.users.notifications.deleted"),
              "success"
            );
            this.getUserList();
          }

          this.dropUser = null;
          Loading.dispatch("reduce");
        });
      }
    },
    updateUser: function() {
      if (Auth.isUserAdmin() && typeof this.users[this.editUser] === "object") {
        Loading.dispatch("increase");
        Users.updateUser(
          this.users[this.editUser].username,
          this.users[this.editUser].name,
          this.users[this.editUser].role
        ).then(result => {
          if (!result) {
            Notification.push(
              "",
              this.$t("admin.users.notifications.notChanged"),
              "danger"
            );
          } else {
            Notification.push(
              "",
              this.$t("admin.users.notifications.changed"),
              "success"
            );
            this.editUser = null;
          }
          Loading.dispatch("reduce");
        });
      }
    },
    updateUserCredentials: function() {
      if (
        Auth.isUserAdmin() &&
        typeof this.users[this.editUserCredentials] === "object"
      ) {
        if (
          typeof this.users[this.editUserCredentials].password ===
            "undefined" ||
          this.users[this.editUserCredentials].password === "" ||
          this.users[this.editUserCredentials].password !==
            this.users[this.editUserCredentials].passwordRe
        ) {
          Notification.push(
            "",
            this.$t("admin.users.notifications.passwordsNotEqual"),
            "danger"
          );
        } else {
          Users.updateUserCredentials(
            this.users[this.editUserCredentials].username,
            this.users[this.editUserCredentials].password
          ).then(result => {
            if (!result) {
              Notification.push(
                "",
                this.$t("admin.users.notifications.notChangedCredentials"),
                "danger"
              );
            } else {
              Notification.push(
                "",
                this.$t("admin.users.notifications.changedCredentials"),
                "success"
              );
              this.editUserCredentials = null;
            }
            Loading.dispatch("reduce");
          });
        }
      }
    }
  },
  watch: {
    "newUser.username": {
      handler: function() {
        this.newUser.username = this.newUser.username.toLowerCase();
      }
    }
  }
};
</script>

<style lang="scss">
.button-margin {
  margin-left: 1em;
}
</style>
